import {
  azElFromEquirectUv,
  equirectPolygonsFromPerspectiveRect,
  projectAzElToPerspective,
  type PhotoBlurRegion,
  type PostPhotoPrivacyAdminDecision,
} from '@citizen-walk/shared-types';

/**
 * 投稿詳細「写真のぼかし」の範囲の編集 (docs/28 §10-1) の pure helper。
 * 座標は画像に対する 0〜1 (360度写真は正距円筒の u/v)。
 */

export function regionKindLabel(r: PhotoBlurRegion): string {
  if (r.source === 'manual' || r.kind === 'manual') return '手で追加';
  return r.kind === 'face' ? '顔' : 'ナンバー';
}

export const ADMIN_DECISION_LABELS: Record<PostPhotoPrivacyAdminDecision['action'], string> = {
  retry: 'もう一度処理した',
  'manual-blur': '手でぼかしを入れて公開した',
  'publish-unblurred': 'ぼかしなしで公開した',
  withhold: '公開しないことにした',
};

/** SVG の polygon points (viewBox 0 0 1 1)。 */
export function regionSvgPoints(r: PhotoBlurRegion): string {
  if (r.shape.type === 'rect') {
    const { x, y, w, h } = r.shape;
    return `${x},${y} ${x + w},${y} ${x + w},${y + h} ${x},${y + h}`;
  }
  return r.shape.points.map((p) => `${p.x},${p.y}`).join(' ');
}

/** ドラッグの始点・終点 (0〜1) → 矩形。小さすぎる (誤クリック) なら null。 */
export function rectFromDrag(
  a: { x: number; y: number },
  b: { x: number; y: number },
  minSize = 0.01,
): { x: number; y: number; w: number; h: number } | null {
  const c = (v: number) => Math.min(1, Math.max(0, v));
  const x0 = c(Math.min(a.x, b.x));
  const y0 = c(Math.min(a.y, b.y));
  const x1 = c(Math.max(a.x, b.x));
  const y1 = c(Math.max(a.y, b.y));
  if (x1 - x0 < minSize || y1 - y0 < minSize) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

export function addManualRegion(
  regions: readonly PhotoBlurRegion[],
  rect: { x: number; y: number; w: number; h: number },
): PhotoBlurRegion[] {
  return [
    ...regions,
    { kind: 'manual', source: 'manual', enabled: true, shape: { type: 'rect', ...rect } },
  ];
}

/** 自動の範囲は有効/無効を切り替える (外す = 復元)。手で追加した範囲は削除する。 */
export function toggleOrRemoveRegion(
  regions: readonly PhotoBlurRegion[],
  index: number,
): PhotoBlurRegion[] {
  const r = regions[index];
  if (!r) return [...regions];
  if (r.source === 'manual') return regions.filter((_, i) => i !== index);
  return regions.map((x, i) => (i === index ? { ...x, enabled: !x.enabled } : x));
}

/** 編集で「ぼかしを外す」(ぼかし前の内容が公開される) 変更があるか。 */
export function hasUnblurChange(
  before: readonly PhotoBlurRegion[],
  after: readonly PhotoBlurRegion[],
): boolean {
  const key = (r: PhotoBlurRegion) => JSON.stringify(r.shape);
  const afterEnabled = new Map(after.map((r) => [key(r), r.enabled]));
  return before.some((r) => r.enabled && afterEnabled.get(key(r)) !== true);
}

/**
 * 360度写真: ビューアで見ている画面上の矩形 (画面に対する 0〜1) → 全天球の多角形の範囲 (手で追加)。
 * 変換式の正本は shared-types `equirectPolygonsFromPerspectiveRect` (ビューアのシェーダと同じ規約)。
 * 左右の端をまたぐ範囲は 2 つに分かれる。`baseHeadingDeg` はビューアに渡したものと同じ値にする。
 */
export function panoramaRectToRegions(
  rect: { x: number; y: number; w: number; h: number },
  view: { heading: number; pitch: number; hfov: number; width: number; height: number },
  baseHeadingDeg = 0,
): PhotoBlurRegion[] {
  const polys = equirectPolygonsFromPerspectiveRect(
    { x0: rect.x, y0: rect.y, x1: rect.x + rect.w, y1: rect.y + rect.h },
    {
      headingDeg: view.heading,
      pitchDeg: view.pitch,
      hfovDeg: view.hfov,
      aspect: view.width / Math.max(1, view.height),
    },
    baseHeadingDeg,
  );
  return polys.map((points) => ({
    kind: 'manual',
    source: 'manual',
    enabled: true,
    shape: { type: 'polygon', points: points.map(([x, y]) => ({ x, y })) },
  }));
}

/**
 * 360度写真: 範囲 (全天球の u/v) → いまの視点の画面上の多角形 (画面に対する 0〜1、SVG の points)。
 * カメラの後ろに回った点は除き、前方の点が 3 つ未満なら描かない (null)。画面からはみ出す点は
 * そのまま返す (SVG 側で切れる)。
 */
export function regionScreenPoints(
  r: PhotoBlurRegion,
  view: { heading: number; pitch: number; hfov: number; width: number; height: number },
  baseHeadingDeg = 0,
): string | null {
  const uv: { x: number; y: number }[] =
    r.shape.type === 'polygon'
      ? r.shape.points
      : [
          { x: r.shape.x, y: r.shape.y },
          { x: r.shape.x + r.shape.w, y: r.shape.y },
          { x: r.shape.x + r.shape.w, y: r.shape.y + r.shape.h },
          { x: r.shape.x, y: r.shape.y + r.shape.h },
        ];
  const cam = {
    headingDeg: view.heading,
    pitchDeg: view.pitch,
    hfovDeg: view.hfov,
    aspect: view.width / Math.max(1, view.height),
  };
  const pts: string[] = [];
  for (const p of uv) {
    const { azimuthDeg, elevationDeg } = azElFromEquirectUv(p.x, p.y, baseHeadingDeg);
    const proj = projectAzElToPerspective(azimuthDeg, elevationDeg, cam);
    if (proj.inFront) pts.push(`${proj.sx},${proj.sy}`);
  }
  return pts.length >= 3 ? pts.join(' ') : null;
}

/** 範囲の線の色 (平面写真の重ね表示と同じ: 赤 = 自動、青 = 手で追加、点線 = 外した範囲)。 */
export function regionStrokeClass(r: PhotoBlurRegion): string {
  if (!r.enabled)
    return 'fill-transparent stroke-slate-400 [stroke-dasharray:4_3] [stroke-width:2]';
  return r.source === 'manual'
    ? 'fill-primary-600/10 stroke-primary-600 [stroke-width:2]'
    : 'fill-danger-600/10 stroke-danger-600 [stroke-width:2]';
}
